// SPDX-License-Identifier: AGPL-3.0-or-later

import {ConfirmModal} from '@app/features/app/components/dialogs/ConfirmModal';
import {CustomStatusDisplay} from '@app/features/app/components/shared/custom_status_display/CustomStatusDisplay';
import {useHover} from '@app/features/app/hooks/useHover';
import Authentication from '@app/features/auth/state/Authentication';
import * as PrivateChannelCommands from '@app/features/channel/commands/PrivateChannelCommands';
import DeveloperOptions from '@app/features/devtools/state/DeveloperOptions';
import GuildMembers from '@app/features/member/state/GuildMembers';
import Permission from '@app/features/permissions/state/Permission';
import {Logger} from '@app/features/platform/utils/AppLogger';
import MemberPresenceSubscription from '@app/features/presence/state/MemberPresenceSubscription';
import Relationships from '@app/features/relationship/state/Relationships';
import {getUserMenuBannerHash, UserImageMenuItems} from '@app/features/ui/action_menu/items/UserImageMenuItems';
import {Button} from '@app/features/ui/button/Button';
import * as ContextMenuCommands from '@app/features/ui/commands/ContextMenuCommands';
import * as ModalCommands from '@app/features/ui/commands/ModalCommands';
import {modal} from '@app/features/ui/commands/ModalCommands';
import * as PopoutCommands from '@app/features/ui/commands/PopoutCommands';
import {Spinner} from '@app/features/ui/components/Spinner';
import FocusRingScope from '@app/features/ui/focus_ring/FocusRingScope';
import {Tooltip} from '@app/features/ui/tooltip/Tooltip';
import * as UserProfileCommands from '@app/features/user/commands/UserProfileCommands';
import {UserProfileModal} from '@app/features/user/components/modals/UserProfileModal';
import {UserSettingsModal} from '@app/features/user/components/modals/UserSettingsModal';
import {UserProfileBadges} from '@app/features/user/components/popouts/UserProfileBadges';
import {UserProfileDataWarning} from '@app/features/user/components/popouts/UserProfileDataWarning';
import styles from '@app/features/user/components/popouts/UserProfilePopout.module.css';
import {
	UserProfileConnections,
	UserProfileMembershipInfo,
	UserProfilePreviewBio,
	UserProfileRoles,
} from '@app/features/user/components/popouts/UserProfileShared';
import {ProfileCardActions} from '@app/features/user/components/profile/profile_card/ProfileCardActions';
import {ProfileCardBanner} from '@app/features/user/components/profile/profile_card/ProfileCardBanner';
import {ProfileCardContent} from '@app/features/user/components/profile/profile_card/ProfileCardContent';
import {ProfileCardFooter} from '@app/features/user/components/profile/profile_card/ProfileCardFooter';
import {ProfileCardLayout} from '@app/features/user/components/profile/profile_card/ProfileCardLayout';
import {ProfileCardUserInfo} from '@app/features/user/components/profile/profile_card/ProfileCardUserInfo';
import {useProfileCardDisplayState} from '@app/features/user/components/profile/useProfileCardDisplayState';
import {VoiceActivitySection} from '@app/features/user/components/profile/VoiceActivitySection';
import {useAutoplayExpandedProfileAnimations} from '@app/features/user/hooks/useAutoplayExpandedProfileAnimations';
import type {Profile} from '@app/features/user/models/Profile';
import type {User} from '@app/features/user/models/User';
import UserProfile from '@app/features/user/state/UserProfile';
import * as NicknameUtils from '@app/features/user/utils/NicknameUtils';
import {createMockProfile} from '@app/features/user/utils/ProfileUtils';
import {Permissions} from '@app/vendor/constants/ChannelConstants';
import {MEDIA_PROXY_PROFILE_BANNER_SIZE_POPOUT} from '@app/vendor/constants/MediaProxyAssetSizes';
import {RelationshipTypes} from '@app/vendor/constants/UserConstants';
import {Trans, useLingui} from '@lingui/react/macro';
import {ChatTeardropIcon, PencilIcon} from '@phosphor-icons/react';
import {observer} from 'mobx-react-lite';
import type React from 'react';
import {useCallback, useEffect, useRef, useState} from 'react';

const logger = new Logger('UserProfilePopout');
interface UserProfilePopoutProps {
	popoutKey: string | number;
	user: User;
	isWebhook: boolean;
	guildId?: string;
	isPreview?: boolean;
}
export const UserProfilePopout: React.FC<UserProfilePopoutProps> = observer(
	({popoutKey, user, isWebhook, guildId, isPreview}) => {
		const {t} = useLingui();
		const [hoverRef, isHovering] = useHover();
		const [profile, setProfile] = useState<Profile | null>(() => {
			const cachedProfile = UserProfile.getProfile(user.id, guildId);
			return cachedProfile ?? createMockProfile(user);
		});
		const [profileLoadError, setProfileLoadError] = useState(false);
		const showProfileDataWarning = !isWebhook && (profileLoadError || DeveloperOptions.forceProfileDataWarning);
		const guildMember = GuildMembers.getMember(profile?.guildId ?? '', user.id);
		const memberRoles = profile?.guildId && guildMember ? guildMember.getSortedRoles() : [];
		const canManageRoles = Permission.can(Permissions.MANAGE_ROLES, {guildId});
		const isCurrentUser = user.id === Authentication.currentUserId;
		const relationshipType = Relationships.getRelationship(user.id)?.type;
		const isBlocked = relationshipType === RelationshipTypes.BLOCKED;
		const openFullProfile = useCallback(
			(autoFocusNote?: boolean) => {
				if (isWebhook) {
					return;
				}
				ModalCommands.push(
					modal(() => <UserProfileModal userId={user.id} guildId={guildId} autoFocusNote={autoFocusNote} />),
				);
				PopoutCommands.close(popoutKey);
			},
			[isWebhook, user.id, guildId, popoutKey],
		);
		useEffect(() => {
			let cancelled = false;
			const cachedProfile = UserProfile.getProfile(user.id, guildId);
			setProfile(cachedProfile ?? createMockProfile(user));
			setProfileLoadError(false);
			if (isWebhook) {
				return () => {
					cancelled = true;
				};
			}
			const fetchProfile = async () => {
				const isGuildMember = guildId ? GuildMembers.getMember(guildId, user.id) : false;
				if (DeveloperOptions.slowProfileLoad) {
					await new Promise((resolve) => setTimeout(resolve, 3000));
				}
				if (cancelled) {
					return;
				}
				try {
					const fetchedProfile = await UserProfileCommands.fetch(user.id, isGuildMember ? guildId : undefined);
					if (cancelled) {
						return;
					}
					setProfile(fetchedProfile);
					setProfileLoadError(false);
				} catch (error) {
					if (cancelled) {
						return;
					}
					logger.error('Failed to fetch profile for user popout', error);
					const nextCachedProfile = UserProfile.getProfile(user.id, guildId);
					setProfile(nextCachedProfile ?? createMockProfile(user));
					setProfileLoadError(true);
				}
			};
			fetchProfile();
			return () => {
				cancelled = true;
			};
		}, [guildId, isWebhook, user]);
		useEffect(() => {
			if (profileLoadError && profile) {
				const profileData = profile?.getEffectiveProfile() ?? null;
				if (profileData && Object.keys(profileData).length > 0) {
					setProfileLoadError(false);
				}
			}
		}, [profileLoadError, profile]);
		useEffect(() => {
			if (!guildId || !user.id || isWebhook) {
				return;
			}
			const hasMember = GuildMembers.getMember(guildId, user.id);
			if (!hasMember) {
				MemberPresenceSubscription.touchMember(guildId, user.id);
				GuildMembers.fetchMembers(guildId, {userIds: [user.id]}).catch((error) => {
					logger.error('Failed to fetch guild member', error);
				});
			} else {
				MemberPresenceSubscription.touchMember(guildId, user.id);
			}
			return () => {
				MemberPresenceSubscription.unsubscribe(guildId, user.id);
			};
		}, [guildId, user.id, isWebhook]);
		const handleClosePopout = useCallback(() => {
			PopoutCommands.close(popoutKey);
		}, [popoutKey]);
		const handleMessage = async () => {
			try {
				PopoutCommands.close(popoutKey);
				await PrivateChannelCommands.openDMChannel(user.id);
			} catch (error) {
				logger.error('Failed to open DM channel', error);
			}
		};
		const handleOpenBlockedDm = () => {
			ModalCommands.push(
				modal(() => (
					<ConfirmModal
						title={t`Open DM`}
						description={t`You blocked ${user.username}. You won't be able to send messages unless you unblock them.`}
						primaryText={t`Open DM`}
						primaryVariant="primary"
						onPrimary={handleMessage}
					/>
				)),
			);
		};
		const handleEditProfile = () => {
			ModalCommands.push(modal(() => <UserSettingsModal initialTab="my_profile" />));
			PopoutCommands.close(popoutKey);
		};
		const handleAvatarContextMenu = useCallback(
			(event: React.MouseEvent) => {
				if (isWebhook || !user.avatar) return;
				ContextMenuCommands.openFromEvent(event, ({onClose}) => (
					<UserImageMenuItems user={user} profile={profile} onClose={onClose} variant="avatar" />
				));
			},
			[isWebhook, user, profile],
		);
		const handleBannerContextMenu = useCallback(
			(event: React.MouseEvent) => {
				if (isWebhook || !getUserMenuBannerHash(user, profile)) return;
				ContextMenuCommands.openFromEvent(event, ({onClose}) => (
					<UserImageMenuItems user={user} profile={profile} onClose={onClose} variant="banner" />
				));
			},
			[isWebhook, user, profile],
		);
		const shouldAutoplayProfileAnimations = useAutoplayExpandedProfileAnimations();
		const {avatarUrl, hoverAvatarUrl, bannerUrl, accentColor, profileData} = useProfileCardDisplayState({
			user,
			profile,
			guildId: profile?.guildId,
			guildMember,
			guildMemberProfile: profile?.guildMemberProfile,
			shouldAutoplayProfileAnimations,
			bannerSize: MEDIA_PROXY_PROFILE_BANNER_SIZE_POPOUT,
		});
		const popoutContainerRef = useRef<HTMLDivElement | null>(null);
		const displayName = NicknameUtils.getNickname(user, guildId);
		if (!profile && !isWebhook) {
			return (
				<div className={styles.loadingContainer}>
					<Spinner />
				</div>
			);
		}
		const borderColor = accentColor;
		const bannerColor = accentColor;
		return (
			<FocusRingScope containerRef={popoutContainerRef}>
				<div ref={popoutContainerRef}>
					<ProfileCardLayout borderColor={borderColor} hoverRef={hoverRef}>
						<ProfileCardBanner
							bannerUrl={bannerUrl as string | null}
							bannerColor={bannerColor}
							user={user}
							avatarUrl={avatarUrl}
							hoverAvatarUrl={hoverAvatarUrl}
							disablePresence={isWebhook}
							isClickable={!isWebhook}
							onAvatarClick={!isWebhook ? () => openFullProfile() : undefined}
							onAvatarContextMenu={handleAvatarContextMenu}
							onBannerContextMenu={handleBannerContextMenu}
						/>
						{!isWebhook && <UserProfileBadges user={user} profile={profile} />}
						<ProfileCardContent isWebhook={isWebhook}>
							{showProfileDataWarning && (
								<div className={styles.profileDataWarning}>
									<UserProfileDataWarning />
								</div>
							)}
							<ProfileCardUserInfo
								displayName={displayName}
								user={user}
								pronouns={profileData?.pronouns}
								showUsername={!isWebhook}
								isClickable={!isWebhook}
								isWebhook={isWebhook}
								onDisplayNameClick={!isWebhook ? () => openFullProfile() : undefined}
								onUsernameClick={!isWebhook ? () => openFullProfile() : undefined}
								actions={
									!isWebhook && (
										<ProfileCardActions
											userId={user.id}
											isHovering={isHovering}
											onNoteClick={() => openFullProfile(true)}
										/>
									)
								}
							/>
							{!isWebhook && (
								<div className={styles.profileCustomStatus}>
									<CustomStatusDisplay
										userId={user.id}
										className={styles.profileCustomStatusText}
										allowJumboEmoji
										maxLines={0}
										alwaysAnimate={shouldAutoplayProfileAnimations}
									/>
								</div>
							)}
							{!isWebhook && <VoiceActivitySection userId={user.id} onNavigate={handleClosePopout} />}
							{profile && (
								<UserProfilePreviewBio
									profile={profile}
									profileData={profileData ?? null}
									onShowMore={() => openFullProfile()}
								/>
							)}
							{profile && <UserProfileMembershipInfo profile={profile} user={user} />}
							{!isWebhook && profile && (
								<UserProfileRoles
									profile={profile}
									user={user}
									memberRoles={[...memberRoles]}
									canManageRoles={canManageRoles}
								/>
							)}
							{profile && <UserProfileConnections profile={profile} variant="compact" />}
						</ProfileCardContent>
						{!isWebhook && (
							<ProfileCardFooter>
								{isCurrentUser ? (
									isPreview ? (
										<Tooltip text={t`You can't message yourself`} maxWidth="xl">
											<div>
												<Button
													small={true}
													leftIcon={<ChatTeardropIcon className={styles.iconSmall} />}
													disabled={true}
												>
													<Trans>Message</Trans>
												</Button>
											</div>
										</Tooltip>
									) : user.isClaimed() ? (
										<Button
											small={true}
											leftIcon={<PencilIcon className={styles.iconSmall} />}
											onClick={handleEditProfile}
										>
											<Trans>Edit Profile</Trans>
										</Button>
									) : null
								) : (
									<Button
										small={true}
										leftIcon={<ChatTeardropIcon className={styles.iconSmall} />}
										onClick={isBlocked ? handleOpenBlockedDm : handleMessage}
									>
										{isBlocked ? <Trans>Open DM</Trans> : <Trans>Message</Trans>}
									</Button>
								)}
							</ProfileCardFooter>
						)}
					</ProfileCardLayout>
				</div>
			</FocusRingScope>
		);
	},
);
